"use client";

import React from "react";
import { WageRate, ROLE_TYPE_LABELS } from "../services/wage-rates-service";

interface WageRatesTableProps {
  rows: WageRate[];
  isLoading?: boolean;
  onEdit?: (row: WageRate) => void;
  onDelete?: (row: WageRate) => void;
}

const formatCurrency = (value: number | string): string => {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  return num.toLocaleString("vi-VN", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

const formatDate = (dateStr: string): string => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  const dd = d.getDate().toString().padStart(2, "0");
  const MM = (d.getMonth() + 1).toString().padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${MM}/${yyyy}`;
};

const WageRatesTable: React.FC<WageRatesTableProps> = ({
  rows,
  isLoading = false,
  onEdit,
  onDelete,
}) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-10">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-500"></div>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-8 text-center text-slate-500">
        Chưa có đơn giá nào được thiết lập
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-xl shadow-sm border border-slate-200">
      <table className="w-full text-sm text-slate-700">
        <thead className="bg-slate-100 text-xs uppercase text-slate-600">
          <tr>
            <th className="px-4 py-3 text-center w-[60px]">STT</th>
            <th className="px-4 py-3 text-left">Loại công việc</th>
            <th className="px-4 py-3 text-left">Nhân viên</th>
            <th className="px-4 py-3 text-left">Chi nhánh</th>
            <th className="px-4 py-3 text-right">Đơn giá (VND/kg)</th>
            <th className="px-4 py-3 text-center">Hiệu lực từ</th>
            <th className="px-4 py-3 text-center">Đến</th>
            <th className="px-4 py-3 text-center">Trạng thái</th>
            <th className="px-4 py-3 text-center w-[120px]">Thao tác</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200">
          {rows.map((row, idx) => (
            <tr
              key={row.id}
              className={`${idx % 2 === 0 ? "bg-white" : "bg-slate-50"} hover:bg-slate-100 transition`}
            >
              <td className="px-4 py-3 text-center font-medium">{idx + 1}</td>
              <td className="px-4 py-3">
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  ({
                    1: "bg-blue-100 text-blue-700",
                    2: "bg-purple-100 text-purple-700",
                    3: "bg-green-100 text-green-700",
                    4: "bg-cyan-100 text-cyan-700",
                    5: "bg-indigo-100 text-indigo-700",
                    6: "bg-orange-100 text-orange-700",
                    7: "bg-rose-100 text-rose-700",
                    8: "bg-teal-100 text-teal-700",
                  } as Record<number, string>)[row.role_type] || "bg-gray-100 text-gray-700"
                }`}>
                  {ROLE_TYPE_LABELS[row.role_type] || "Không xác định"}
                </span>
              </td>
              <td className="px-4 py-3 font-medium text-slate-800">
                {row.employee?.name ?? (row.employee_id ? `#${row.employee_id}` : "Tất cả")}
              </td>
              <td className="px-4 py-3">
                {row.branch?.name ?? (row.branch_id ? `#${row.branch_id}` : "Tất cả")}
              </td>
              <td className="px-4 py-3 text-right font-semibold text-green-600">
                {formatCurrency(row.rate_per_kg)}đ/kg
              </td>
              <td className="px-4 py-3 text-center">{formatDate(row.effective_from)}</td>
              <td className="px-4 py-3 text-center">{row.effective_to ? formatDate(row.effective_to) : "—"}</td>
              <td className="px-4 py-3 text-center">
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  row.status === 1 ? "bg-green-100 text-green-700" : "bg-slate-200 text-slate-600"
                }`}>
                  {row.status === 1 ? "Hoạt động" : "Tạm ngưng"}
                </span>
              </td>
              <td className="px-4 py-3 text-center">
                <div className="flex items-center justify-center gap-2">
                  {onEdit && (
                    <button
                      onClick={() => onEdit(row)}
                      className="text-blue-600 hover:text-blue-800 p-1"
                      title="Sửa"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
                      </svg>
                    </button>
                  )}
                  {onDelete && (
                    <button
                      onClick={() => onDelete(row)}
                      className="text-red-600 hover:text-red-800 p-1"
                      title="Xóa"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                      </svg>
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default WageRatesTable;
